import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useThemePreferences } from '../theme/ThemeContext';
import { isDyslexiaTheme } from '../theme/theme';
import {
  LayoutDashboard,
  ClipboardList,
  Calendar,
  TrendingUp,
  Image as ImageIcon,
  Brain,
  Settings,
  LogOut,
  Menu,
  Clock,
  CalendarClock,
  BarChart3,
  LineChart,
  Mic,
  Users,
  Bot,
  ShieldCheck,
  Building2,
  Package,
  Mail,
  Lock,
  CreditCard,
  Landmark,
  UserCircle2,
  Webhook,
  Cable,
  Gavel,
  ArrowLeftRight,
  Shield,
  BookOpen,
  Lightbulb,
  Sun,
  Moon,
  Bell,
  CheckCheck,
  Search,
  FolderHeart,
  Bug,
} from 'lucide-react';
import { HubConnectionBuilder, HubConnection, HubConnectionState } from '@microsoft/signalr';
import { workItemsApi, dailyPlanApi, calendarSourcesApi, authApi, notificationsApi, searchApi } from '../api/services';
import type { WorkItem } from '../types/models';
import BrandMark from './BrandMark';
import SupportDialog from './SupportDialog';
import { usePrincipal } from '../context/PrincipalContext';
import { useUnifiedPlanVariant } from '../hooks/useUnifiedPlanVariant';
import { SlimRail } from './SlimRail';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ToastProvider } from '@/components/ui/toast';
import { ConfirmDialogProvider } from '@/components/ui/confirm-dialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectSeparator,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const MY_VIEW_SENTINEL = '__my_view__';

interface LocalNotification {
  id: string;
  message: string;
  time: string;
  action?: { label: string; path: string };
}

interface ApiNotification {
  id: string;
  title: string;
  message?: string;
  type: string;
  entityType?: string;
  entityId?: string;
  isRead: boolean;
  createdAt: string;
}

interface SearchResult {
  type: string;
  title: string;
  subtitle?: string;
  path: string;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  section?: string;
}

const tenantNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: <LayoutDashboard className="h-4 w-4" />, section: 'TODAY' },
  { label: 'Plan', path: '/plan', icon: <CalendarClock className="h-4 w-4" />, section: 'TODAY' },
  { label: 'Focus Time', path: '/focus', icon: <Clock className="h-4 w-4" />, section: 'TODAY' },
  { label: 'Reflections', path: '/reflections', icon: <BookOpen className="h-4 w-4" />, section: 'TODAY' },
  { label: 'Work Items', path: '/work-items', icon: <ClipboardList className="h-4 w-4" />, section: 'WORK' },
  { label: 'Calendar', path: '/calendar', icon: <Calendar className="h-4 w-4" />, section: 'WORK' },
  { label: 'Transcripts', path: '/transcripts', icon: <Mic className="h-4 w-4" />, section: 'WORK' },
  { label: 'Strategic Drivers', path: '/strategy', icon: <TrendingUp className="h-4 w-4" />, section: 'WORK' },
  { label: 'Projects', path: '/projects', icon: <FolderHeart className="h-4 w-4" />, section: 'WORK' },
  { label: 'Ideas', path: '/ideas', icon: <Lightbulb className="h-4 w-4" />, section: 'WORK' },
  { label: 'Image Capture', path: '/images', icon: <ImageIcon className="h-4 w-4" />, section: 'WORK' },
  { label: 'Context Engine', path: '/context', icon: <Brain className="h-4 w-4" />, section: 'WORK' },
  { label: 'Principal Profile', path: '/principal-profile', icon: <UserCircle2 className="h-4 w-4" />, section: 'WORK' },
  { label: 'Decisions', path: '/decisions', icon: <Gavel className="h-4 w-4" />, section: 'WORK' },
  { label: 'Delegation', path: '/delegation', icon: <ArrowLeftRight className="h-4 w-4" />, section: 'WORK' },
  { label: 'Horizon', path: '/horizon', icon: <LineChart className="h-4 w-4" />, section: 'INTELLIGENCE' },
  { label: 'Reports', path: '/reports', icon: <BarChart3 className="h-4 w-4" />, section: 'INTELLIGENCE' },
  { label: 'EA Workspace', path: '/ea-workspace', icon: <Users className="h-4 w-4" />, section: 'EA WORKSPACE' },
  { label: 'Preferences', path: '/preferences', icon: <Settings className="h-4 w-4" />, section: 'CONFIGURE' },
  { label: 'Feature Requests', path: '/feature-requests', icon: <Lightbulb className="h-4 w-4" />, section: 'CONFIGURE' },
  { label: 'Prompt Management', path: '/prompt-management', icon: <Bot className="h-4 w-4" />, section: 'CONFIGURE' },
  { label: 'Webhooks', path: '/webhooks', icon: <Webhook className="h-4 w-4" />, section: 'CONFIGURE' },
  { label: 'Connections', path: '/connections', icon: <Cable className="h-4 w-4" />, section: 'CONFIGURE' },
  { label: 'Audit Log', path: '/audit-log', icon: <Shield className="h-4 w-4" />, section: 'CONFIGURE' },
  { label: 'Organisation', path: '/organisation', icon: <Landmark className="h-4 w-4" />, section: 'CONFIGURE' },
  { label: 'Admin', path: '/admin', icon: <ShieldCheck className="h-4 w-4" />, section: 'CONFIGURE' },
  { label: 'Billing', path: '/billing', icon: <CreditCard className="h-4 w-4" />, section: 'CONFIGURE' },
];

const platformNavItems: NavItem[] = [
  { label: 'Platform Overview', path: '/platform', icon: <BarChart3 className="h-4 w-4" />, section: 'PLATFORM' },
  { label: 'Tenants', path: '/platform/tenants', icon: <Building2 className="h-4 w-4" />, section: 'PLATFORM' },
  { label: 'Billing Centre', path: '/platform/billing', icon: <CreditCard className="h-4 w-4" />, section: 'BILLING' },
  { label: 'Products & Pricing', path: '/platform/products', icon: <Package className="h-4 w-4" />, section: 'BILLING' },
  { label: 'Email Settings', path: '/platform/email', icon: <Mail className="h-4 w-4" />, section: 'BILLING' },
  { label: 'Security & Diagnostics', path: '/platform/security', icon: <Lock className="h-4 w-4" />, section: 'SYSTEM' },
  { label: 'Bug Reports', path: '/platform/bug-reports', icon: <Bug className="h-4 w-4" />, section: 'SYSTEM' },
];

function AppLayoutInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, ensureValidToken } = useAuth();
  const { preferences: themePrefs, setPreferences: setThemePrefs, resolvedMode } = useThemePreferences();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [mfaBannerDismissed, setMfaBannerDismissed] = useState(false);
  const { enabled: unifiedPlanEnabled } = useUnifiedPlanVariant();

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [localNotifications, setLocalNotifications] = useState<LocalNotification[]>([]);
  const [apiNotifications, setApiNotifications] = useState<ApiNotification[]>([]);
  const [tenantFeatures, setTenantFeatures] = useState<Record<string, boolean>>({});
  const hubConnectionRef = useRef<HubConnection | null>(null);

  const workItemsCacheRef = useRef<{ items: WorkItem[]; fetchedAt: number } | null>(null);
  const workItemsInflightRef = useRef<Promise<WorkItem[]> | null>(null);
  const WORK_ITEMS_CACHE_TTL_MS = 60_000;
  const getCachedWorkItems = useCallback(async (): Promise<WorkItem[]> => {
    const now = Date.now();
    const cached = workItemsCacheRef.current;
    if (cached && now - cached.fetchedAt < WORK_ITEMS_CACHE_TTL_MS) {
      return cached.items;
    }
    if (workItemsInflightRef.current) return workItemsInflightRef.current;
    const promise = workItemsApi.getAll()
      .then((items) => {
        workItemsCacheRef.current = { items, fetchedAt: Date.now() };
        return items;
      })
      .finally(() => {
        workItemsInflightRef.current = null;
      });
    workItemsInflightRef.current = promise;
    return promise;
  }, []);

  const [globalSearch, setGlobalSearch] = useState('');
  const [searchPopoverOpen, setSearchPopoverOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [showShortcutsDialog, setShowShortcutsDialog] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'n' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        navigate('/work-items?action=create');
      }
      if (e.key === 't' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        navigate('/');
      }
      if (e.key === 'c' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        navigate('/calendar');
      }
      if (e.key === '?' && !e.ctrlKey) {
        e.preventDefault();
        setShowShortcutsDialog(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      const timer = setTimeout(() => {
        Notification.requestPermission();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const { activePrincipal, setActivePrincipal, clearPrincipal, principals, isActingOnBehalf } = usePrincipal();

  const handlePrincipalChange = useCallback((value: string) => {
    if (value === MY_VIEW_SENTINEL) {
      clearPrincipal();
    } else {
      const selected = principals.find(p => p.id === value);
      if (selected) {
        setActivePrincipal(selected);
      }
    }
  }, [principals, setActivePrincipal, clearPrincipal]);

  const isPlatformAdmin = user?.role === 'PlatformAdmin';
  const isEaRole = user?.role === 'ExecutiveAssistant';
  const isAdmin = user?.role === 'Admin' || user?.role === 'TenantAdmin' || user?.isTenantAdmin;
  const mfaSuggested = sessionStorage.getItem('mfaSuggested') === 'true';
  const mfaRequired = sessionStorage.getItem('mfaRequired') === 'true';
  const showMfaBanner = user && (mfaSuggested || mfaRequired) && !mfaBannerDismissed;

  useEffect(() => {
    if (!user || isPlatformAdmin) return;
    authApi.me().then((me: any) => {
      if (me.features) setTenantFeatures(me.features);
    }).catch(() => {});
  }, [user, isPlatformAdmin]);

  useEffect(() => {
    if (isPlatformAdmin && mfaRequired && location.pathname !== '/mfa-setup') {
      navigate('/mfa-setup', { replace: true });
    }
  }, [isPlatformAdmin, mfaRequired, location.pathname, navigate]);

  useEffect(() => {
    if (!user || isPlatformAdmin) return;
    const computeNotifications = async () => {
      const notifs: LocalNotification[] = [];
      const now = new Date();
      try {
        const workItems = await getCachedWorkItems();
        const staleItems = workItems.filter(wi => {
          if (!wi.isCarryForward || wi.status === 'Done' || wi.status === 'Cancelled') return false;
          const orig = wi.originalDate ? new Date(wi.originalDate) : new Date(wi.createdAt);
          const daysDiff = Math.floor((now.getTime() - orig.getTime()) / (1000 * 60 * 60 * 24));
          return daysDiff >= 3;
        });
        if (staleItems.length > 0) {
          notifs.push({
            id: 'carry-forward-stale',
            message: `${staleItems.length} item${staleItems.length !== 1 ? 's have' : ' has'} been carried forward for 3+ days`,
            time: 'Review overdue items',
            action: { label: 'View Work Items', path: '/work-items' },
          });
        }

        const today = now.toISOString().split('T')[0];
        try {
          const plan = await dailyPlanApi.getByDate(today);
          if (!plan || plan.status === 'Draft') {
            notifs.push({
              id: 'plan-not-started',
              message: "You haven't started your day yet",
              time: 'Start your day to get a planned schedule',
              action: { label: 'Go to Dashboard', path: '/' },
            });
          }
        } catch {
          notifs.push({
            id: 'plan-not-started',
            message: "You haven't started your day yet",
            time: 'Start your day to get a planned schedule',
            action: { label: 'Go to Dashboard', path: '/' },
          });
        }
      } catch {
        // ignore
      }

      if (user && !user.mfaEnabled) {
        notifs.push({
          id: 'mfa-not-enabled',
          message: 'Secure your account with MFA',
          time: 'Multi-factor authentication adds an extra layer of security',
          action: { label: 'Set up MFA', path: '/mfa-setup' },
        });
      }

      try {
        const sources = await calendarSourcesApi.getAll();
        if (!sources || sources.length === 0) {
          notifs.push({
            id: 'calendar-not-connected',
            message: 'Connect your calendar for better planning',
            time: 'Calendar integration enables time-aware scheduling',
            action: { label: 'Go to Calendar', path: '/calendar' },
          });
        }
      } catch {
        // ignore
      }

      setLocalNotifications(notifs);
    };
    computeNotifications();
  }, [user, isPlatformAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || isPlatformAdmin) return;

    notificationsApi.getAll(false).then(data => {
      setApiNotifications(data);
    }).catch(() => {});

    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://localhost:7001/api').replace(/\/api$/, '');
    const connection = new HubConnectionBuilder()
      .withUrl(`${apiBaseUrl}/hubs/notifications`, {
        accessTokenFactory: () => ensureValidToken(),
      })
      .withAutomaticReconnect()
      .build();

    hubConnectionRef.current = connection;

    connection.on('NotificationReceived', (notification: ApiNotification) => {
      setApiNotifications(prev => [{ ...notification, isRead: false }, ...prev]);

      if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'SHOW_NOTIFICATION',
            title: notification.title,
            body: notification.message || '',
            tag: `cadence-${notification.id}`,
          });
        } else {
          new Notification(notification.title, {
            body: notification.message || '',
            icon: '/logo192.png',
            tag: `cadence-${notification.id}`,
          });
        }
      }
    });

    connection.on('CalendarSynced', () => {
      window.dispatchEvent(new CustomEvent('cadence:calendar-synced'));
    });

    connection.on('TranscriptAnalysed', (payload: { transcriptId: string; success: boolean; workItemsCreated: number; message?: string }) => {
      window.dispatchEvent(new CustomEvent('cadence:transcript-analysed', { detail: payload }));
    });

    connection.start().catch(err => {
      console.warn('SignalR connection failed:', err);
    });

    return () => {
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop();
      }
      hubConnectionRef.current = null;
    };
  }, [user, isPlatformAdmin, ensureValidToken]);

  const handleGlobalSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchPopoverOpen(false);
      return;
    }
    setSearchLoading(true);
    setSearchPopoverOpen(true);
    try {
      const hits = await searchApi.query(query.trim(), undefined, 25);
      const results: SearchResult[] = hits.map((h) => ({
        type: h.type as SearchResult['type'],
        title: h.title,
        subtitle: h.subtitle,
        path: h.path,
      }));
      setSearchResults(results);
    } catch {
      setSearchResults([]);
    }
    setSearchLoading(false);
  }, []);

  const unreadApiCount = apiNotifications.filter(n => !n.isRead).length;
  const notificationCount = localNotifications.length + unreadApiCount;

  const handleMarkAsRead = useCallback(async (id: string) => {
    try {
      await notificationsApi.markAsRead(id);
      setApiNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    } catch {
      // ignore
    }
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await notificationsApi.markAllAsRead();
      setApiNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch {
      // ignore
    }
  }, []);

  const displayName = user?.displayName || user?.email || 'User';

  let navItems: NavItem[];
  if (isPlatformAdmin) {
    navItems = platformNavItems;
  } else {
    navItems = tenantNavItems.filter(item => {
      if (item.path === '/plan' && !unifiedPlanEnabled) return false;
      if (item.section === 'EA WORKSPACE' && !isEaRole) return false;
      if (item.path === '/principal-profile' && !isEaRole && !isAdmin) return false;
      if (item.path === '/prompt-management' && !isAdmin) return false;
      if (item.path === '/organisation' && !isAdmin) return false;
      if (item.path === '/admin' && !isAdmin) return false;
      if (item.path === '/billing' && !isAdmin) return false;
      if (item.path === '/webhooks' && !isAdmin) return false;
      if (item.path === '/projects' && tenantFeatures.projectsEnabled === false) return false;
      return true;
    });
  }

  const productName = isPlatformAdmin ? 'Cadence Platform' : 'Cadence';
  const tagline = isPlatformAdmin ? 'Platform administration' : 'Executive orchestration platform';

  const renderDrawerContents = () => {
    let lastSection = '';
    return (
      <div className="flex h-full flex-col">
        <div className="px-5 pb-2 pt-5">
          {isPlatformAdmin ? (
            <div className="flex items-center gap-2">
              <div
                className="flex h-7 w-7 items-center justify-center rounded-md text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)' }}
              >
                C
              </div>
              <span className="text-lg font-bold tracking-tight text-rose-600 dark:text-rose-400">
                {productName}
              </span>
            </div>
          ) : (
            <BrandMark size="sm" label={productName} />
          )}
          <p className="ml-9 text-xs text-muted-foreground">{tagline}</p>
        </div>
        <div className="mx-4 mt-2 border-t border-border" />
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          {navItems.map((item) => {
            const showSection = item.section && item.section !== lastSection;
            if (item.section) lastSection = item.section;
            const isSelected = location.pathname === item.path;
            return (
              <React.Fragment key={item.path + item.label}>
                {showSection && (
                  <div className="block px-3 pb-1 pt-4 text-[0.65rem] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                    {item.section}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => { navigate(item.path); setMobileOpen(false); }}
                  className={cn(
                    'mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    isSelected
                      ? isPlatformAdmin
                        ? 'bg-rose-500/10 font-semibold text-rose-600 dark:text-rose-400'
                        : 'bg-primary/10 font-semibold text-primary'
                      : 'text-foreground/85 hover:bg-accent hover:text-foreground',
                  )}
                >
                  <span className={cn('flex h-5 w-5 items-center justify-center', isSelected ? '' : 'text-muted-foreground')}>
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </nav>
        <div className="mx-4 border-t border-border" />
        <div className="px-3 pt-2">
          <button
            type="button"
            onClick={() => setSupportOpen(true)}
            className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground/85 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Bug className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Report a problem</span>
          </button>
        </div>
        <div className="px-3 pb-2 pt-1">
          <button
            type="button"
            onClick={() =>
              setThemePrefs({
                ...themePrefs,
                mode: resolvedMode === 'dark' ? 'light' : 'dark',
              })
            }
            className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground/85 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {resolvedMode === 'dark'
              ? <Sun className="h-4 w-4 text-muted-foreground" />
              : <Moon className="h-4 w-4 text-muted-foreground" />}
            <span className="font-medium">
              {resolvedMode === 'dark' ? 'Switch to light' : 'Switch to dark'}
            </span>
          </button>
        </div>
        <div className="mx-4 border-t border-border" />
        <div className="flex items-center gap-3 p-4">
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold text-white',
              isPlatformAdmin ? 'bg-rose-700' : 'bg-primary',
            )}
          >
            {displayName.charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{displayName}</p>
            <p className="truncate text-[0.7rem] text-muted-foreground">{user?.role}</p>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={logout}
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex min-h-screen bg-background">
        <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center gap-2 border-b border-border bg-background/95 px-3 backdrop-blur md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span
            className={cn(
              'flex-1 text-lg font-bold tracking-tight',
              isPlatformAdmin ? 'text-rose-600 dark:text-rose-400' : 'text-primary',
            )}
          >
            {productName}
          </span>
          {isEaRole && principals.length > 0 && (
            <Select
              value={activePrincipal?.id || MY_VIEW_SENTINEL}
              onValueChange={handlePrincipalChange}
            >
              <SelectTrigger
                className={cn(
                  'mr-1 h-8 min-w-[140px] text-xs',
                  isActingOnBehalf && 'bg-primary/10',
                )}
              >
                <SelectValue placeholder="My View" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={MY_VIEW_SENTINEL}>My View</SelectItem>
                <SelectSeparator />
                {principals.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!isPlatformAdmin && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setMobileSearchOpen(true)}
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </Button>
          )}
          {!isPlatformAdmin && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setNotificationsOpen(true)}
              aria-label="Notifications"
              className="relative"
            >
              <Bell className="h-5 w-5" />
              {notificationCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold leading-none text-white">
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </Button>
          )}
        </header>

        <Dialog open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
          <DialogContent className="md:hidden">
            <DialogHeader>
              <DialogTitle>Search Cadence</DialogTitle>
            </DialogHeader>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                placeholder="Work items, drivers, decisions, transcripts..."
                value={globalSearch}
                onChange={e => setGlobalSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleGlobalSearch(globalSearch);
                  }
                }}
                className="pl-9"
              />
            </div>
            <p className="text-xs text-muted-foreground">Press Enter to search</p>
          </DialogContent>
        </Dialog>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-72 p-0 sm:max-w-xs md:hidden">
            {renderDrawerContents()}
          </SheetContent>
        </Sheet>

        <nav className="hidden md:block md:w-[72px] md:flex-shrink-0">
          <SlimRail
            isPlatformAdmin={!!isPlatformAdmin}
            themeMode={resolvedMode}
            onToggleTheme={() =>
              setThemePrefs({
                ...themePrefs,
                mode: resolvedMode === 'dark' ? 'light' : 'dark',
              })
            }
            onReportBug={() => setSupportOpen(true)}
            onLogout={logout}
            userInitial={(displayName || 'U').charAt(0).toUpperCase()}
            userDisplayName={displayName}
            userRole={user?.role || ''}
            isVisible={(path: string) => {
              if (isPlatformAdmin) {
                return platformNavItems.some(p => p.path === path);
              }
              if (path === '/plan' && !unifiedPlanEnabled) return false;
              if (path === '/ea-workspace' && !isEaRole) return false;
              if (path === '/principal-profile' && !isEaRole && !isAdmin) return false;
              if (path === '/prompt-management' && !isAdmin) return false;
              if (path === '/organisation' && !isAdmin) return false;
              if (path === '/admin' && !isAdmin) return false;
              if (path === '/billing' && !isAdmin) return false;
              if (path === '/webhooks' && !isAdmin) return false;
              if (path === '/projects' && tenantFeatures.projectsEnabled === false) return false;
              return true;
            }}
          />
        </nav>

        <main
          className={cn(
            'max-w-full flex-1 overflow-auto p-3 pt-16 sm:p-4 md:p-6 md:pt-6',
            isDyslexiaTheme(themePrefs.fontFamily) && 'text-left dyslexia-prose',
          )}
        >
          {!isPlatformAdmin && (
            <div className="mb-3 hidden items-center gap-3 md:flex">
              <Popover open={searchPopoverOpen && (searchResults.length > 0 || searchLoading)} onOpenChange={setSearchPopoverOpen}>
                <PopoverAnchor asChild>
                  <div className="relative max-w-[360px] flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={searchInputRef}
                      placeholder="Search work items, drivers..."
                      value={globalSearch}
                      onChange={e => setGlobalSearch(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          handleGlobalSearch(globalSearch);
                        }
                      }}
                      className="rounded-full bg-white/55 pl-9 text-[0.85rem] backdrop-blur dark:bg-white/5"
                    />
                  </div>
                </PopoverAnchor>
                <PopoverContent
                  align="start"
                  side="bottom"
                  className="w-[400px] max-h-[400px] overflow-auto p-3"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  {searchLoading ? (
                    <p className="text-sm text-muted-foreground">Searching...</p>
                  ) : searchResults.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No results found</p>
                  ) : (
                    (() => {
                      const grouped: Record<string, SearchResult[]> = {};
                      searchResults.forEach(r => {
                        if (!grouped[r.type]) grouped[r.type] = [];
                        grouped[r.type].push(r);
                      });
                      return Object.entries(grouped).map(([type, items]) => (
                        <div key={type} className="mb-3 last:mb-0">
                          <p className="mb-1 block text-xs font-semibold text-muted-foreground">
                            {type}s ({items.length})
                          </p>
                          {items.map((item, i) => (
                            <div
                              key={i}
                              onClick={() => {
                                navigate(item.path);
                                setSearchPopoverOpen(false);
                                setSearchResults([]);
                                setGlobalSearch('');
                              }}
                              className="cursor-pointer rounded-md p-2 transition-colors hover:bg-accent"
                            >
                              <p className="text-sm font-medium">{item.title}</p>
                              {item.subtitle && (
                                <p className="text-xs text-muted-foreground">{item.subtitle}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ));
                    })()
                  )}
                </PopoverContent>
              </Popover>
              <div className="flex-1" />
              {isEaRole && principals.length > 0 && (
                <Select
                  value={activePrincipal?.id || MY_VIEW_SENTINEL}
                  onValueChange={handlePrincipalChange}
                >
                  <SelectTrigger
                    className={cn(
                      'min-w-[200px] text-[0.85rem]',
                      isActingOnBehalf && 'bg-primary/10',
                    )}
                  >
                    <SelectValue placeholder="My View" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={MY_VIEW_SENTINEL}>My View</SelectItem>
                    <SelectSeparator />
                    {principals.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <div className="flex flex-col">
                          <span className="text-sm">{p.name}</span>
                          <span className="text-xs text-muted-foreground">{p.email}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Notifications"
                    className="relative"
                  >
                    <Bell className="h-5 w-5" />
                    {notificationCount > 0 && (
                      <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold leading-none text-white">
                        {notificationCount > 99 ? '99+' : notificationCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" side="bottom" className="w-[380px] p-3">
                  <NotificationsPanel
                    apiNotifications={apiNotifications}
                    localNotifications={localNotifications}
                    unreadApiCount={unreadApiCount}
                    onMarkAllAsRead={handleMarkAllAsRead}
                    onMarkAsRead={handleMarkAsRead}
                    onNavigate={(path) => {
                      navigate(path);
                      setNotificationsOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          <Sheet
            open={notificationsOpen && (typeof window === 'undefined' || window.innerWidth < 768)}
            onOpenChange={setNotificationsOpen}
          >
            <SheetContent side="right" className="w-[380px] sm:max-w-md md:hidden">
              <div className="pt-2">
                <NotificationsPanel
                  apiNotifications={apiNotifications}
                  localNotifications={localNotifications}
                  unreadApiCount={unreadApiCount}
                  onMarkAllAsRead={handleMarkAllAsRead}
                  onMarkAsRead={handleMarkAsRead}
                  onNavigate={(path) => {
                    navigate(path);
                    setNotificationsOpen(false);
                  }}
                />
              </div>
            </SheetContent>
          </Sheet>

          {isActingOnBehalf && activePrincipal && (
            <Alert variant="info" className="mb-3">
              <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  Acting on behalf of <strong>{activePrincipal.name}</strong>. All changes will be applied to their account.
                </span>
                <Button variant="outline" size="sm" onClick={clearPrincipal}>
                  Back to My View
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {showMfaBanner && (
            <Alert variant={isPlatformAdmin || isAdmin ? 'warning' : 'info'} className="mb-3">
              <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  {isPlatformAdmin || isAdmin
                    ? 'Multi-factor authentication is required for administrator accounts. Set up MFA now.'
                    : 'Secure your account — set up multi-factor authentication to protect your data.'}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigate('/mfa-setup')}>
                    Set up MFA
                  </Button>
                  {!isPlatformAdmin && !isAdmin && (
                    <Button variant="ghost" size="sm" onClick={() => setMfaBannerDismissed(true)}>
                      Dismiss
                    </Button>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          <Outlet />

          <SupportDialog open={supportOpen} onClose={() => setSupportOpen(false)} />

          <Dialog open={showShortcutsDialog} onOpenChange={setShowShortcutsDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Keyboard Shortcuts</DialogTitle>
              </DialogHeader>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b border-border/40">
                    <td className="py-2 pr-4"><code className="rounded bg-muted px-1.5 py-0.5 text-xs">t</code></td>
                    <td className="py-2">Go to Today/Dashboard</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="py-2 pr-4"><code className="rounded bg-muted px-1.5 py-0.5 text-xs">c</code></td>
                    <td className="py-2">Go to Calendar</td>
                  </tr>
                  <tr className="border-b border-border/40">
                    <td className="py-2 pr-4"><code className="rounded bg-muted px-1.5 py-0.5 text-xs">n</code></td>
                    <td className="py-2">New Work Item</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4"><code className="rounded bg-muted px-1.5 py-0.5 text-xs">?</code></td>
                    <td className="py-2">Show this help</td>
                  </tr>
                </tbody>
              </table>
            </DialogContent>
          </Dialog>

        </main>
      </div>
    </TooltipProvider>
  );
}

// Wrap the layout in ToastProvider so any page rendered inside <Outlet />
// can call useToast() without each page having to mount its own viewport.
export default function AppLayoutWithProviders() {
  return (
    <ToastProvider>
      <ConfirmDialogProvider>
        <AppLayoutInner />
      </ConfirmDialogProvider>
    </ToastProvider>
  );
}

interface NotificationsPanelProps {
  apiNotifications: ApiNotification[];
  localNotifications: LocalNotification[];
  unreadApiCount: number;
  onMarkAllAsRead: () => void;
  onMarkAsRead: (id: string) => void;
  onNavigate: (path: string) => void;
}

function NotificationsPanel({
  apiNotifications,
  localNotifications,
  unreadApiCount,
  onMarkAllAsRead,
  onMarkAsRead,
  onNavigate,
}: NotificationsPanelProps) {
  return (
    <div className="max-h-[70vh] overflow-y-auto">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-semibold">Notifications</h2>
        {unreadApiCount > 0 && (
          <Button variant="ghost" size="sm" onClick={onMarkAllAsRead}>
            <CheckCheck className="h-4 w-4" />
            Mark all read
          </Button>
        )}
      </div>

      {apiNotifications.length > 0 && (
        <div className="flex flex-col gap-2">
          {apiNotifications.map(n => {
            const isCoaching = n.type === 'CoachingNudge';
            const isAlert = n.type === 'Alert' || n.type === 'Overdue';
            const isWarning = n.type === 'Warning' || n.type === 'KpiAlert';
            const borderColour = isCoaching
              ? 'border-l-sky-500'
              : isAlert
                ? 'border-l-rose-500'
                : isWarning
                  ? 'border-l-amber-500'
                  : 'border-l-primary';
            const badgeVariant: 'default' | 'success' | 'warning' | 'danger' | 'muted' = isCoaching
              ? 'default'
              : isAlert
                ? 'danger'
                : isWarning
                  ? 'warning'
                  : 'default';
            return (
              <Card
                key={n.id}
                onClick={() => { if (!n.isRead) onMarkAsRead(n.id); }}
                className={cn(
                  'cursor-pointer border-l-[3px] p-3 shadow-none transition-colors',
                  n.isRead ? 'bg-transparent border-l-transparent' : `bg-accent/50 ${borderColour}`,
                )}
              >
                <CardContent className="p-0">
                  <div className="flex items-center gap-2">
                    {isCoaching && <Lightbulb className="h-4 w-4 text-sky-500" />}
                    <Badge variant={badgeVariant} className="h-5 text-[10px] uppercase">
                      {isCoaching ? 'Coaching' : n.type}
                    </Badge>
                    {!n.isRead && (
                      <span
                        className={cn(
                          'h-2 w-2 flex-shrink-0 rounded-full',
                          isCoaching ? 'bg-sky-500' : 'bg-primary',
                        )}
                      />
                    )}
                  </div>
                  <p className={cn('mt-1 text-sm', n.isRead ? 'font-normal' : 'font-semibold')}>
                    {n.title}
                  </p>
                  {n.message && (
                    <p className="mt-0.5 block text-xs text-muted-foreground">{n.message}</p>
                  )}
                  <p className="mt-1 block text-xs text-muted-foreground/70">
                    {new Date(n.createdAt).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {localNotifications.length > 0 && (
        <>
          {apiNotifications.length > 0 && <div className="my-3 border-t border-border" />}
          <p className="mb-1 block text-[0.65rem] font-medium uppercase tracking-wider text-muted-foreground">
            Suggestions
          </p>
          <div className="flex flex-col gap-2">
            {localNotifications.map(n => (
              <Card
                key={n.id}
                onClick={() => { if (n.action) onNavigate(n.action.path); }}
                className={cn('p-3 shadow-none', n.action ? 'cursor-pointer' : 'cursor-default')}
              >
                <CardContent className="p-0">
                  <p className="text-sm">{n.message}</p>
                  <p className="text-xs text-muted-foreground">{n.time}</p>
                  {n.action && (
                    <Badge variant="outline" className="mt-1 inline-flex">
                      {n.action.label}
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {apiNotifications.length === 0 && localNotifications.length === 0 && (
        <p className="mt-3 text-center text-sm text-muted-foreground">No notifications</p>
      )}
    </div>
  );
}
